import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n, useTranslate } from '../../contexts/I18nContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useSiteLogo } from '../../contexts/SiteLogoContext';
import { Loader2, Mail, Lock, ArrowLeft } from 'lucide-react';

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();
  const { translateText } = useTranslate();
  const { getFullLogoUrl, siteName } = useSiteLogo();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      
      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'provider') {
        navigate('/provider');
      } else if (user.role === 'referrer') {
        navigate('/referrer');
      } else {
        navigate('/');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msgMap = { 'Invalid credentials': 'Credenciales inválidas' };
      const spanishMsg = msgMap[detail] || detail || 'Error al iniciar sesión';
      
      const locale = localStorage.getItem('locale') || 'es';
      if (locale === 'es') {
        setError(spanishMsg);
      } else {
        setError(spanishMsg);
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/translate/batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ texts: [spanishMsg], target_lang: locale })
          });
          const data = await res.json();
          setError(data.translations?.[spanishMsg] || spanishMsg);
        } catch { /* keep spanish */ }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          className="mb-6 rounded-full"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>

        <Card className="rounded-3xl shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center mx-auto mb-4">
              {getFullLogoUrl() ? (
                <img src={getFullLogoUrl()} alt={siteName} className="h-14 object-contain" />
              ) : (
                <div className="w-24 h-24 bg-[#00CC66] rounded-2xl flex items-center justify-center">
                  <span className="text-white font-bold text-3xl">{siteName.charAt(0)}</span>
                </div>
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-[#064E3B]">
              {t('loginTitle')}
            </CardTitle>
            <CardDescription>
              {translateText('Ingresa a tu cuenta para continuar')}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="rounded-xl">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 rounded-xl"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {translateText('Ingresando...')}
                  </>
                ) : (
                  t('loginTitle')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                {t('noAccount')}{' '}
                <Link to="/register" className="text-[#00CC66] font-medium hover:underline">
                  {t('register')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
